import * as THREE from 'three';

export interface WebGPUUnsupportedMaterial {
  uuid: string;
  name: string;
  type: string;
  objectPath: string;
  reasons: string[];
}

export interface WebGPUCompatibilityReport {
  unsupportedMaterials: WebGPUUnsupportedMaterial[];
}

const DEFAULT_ON_BEFORE_COMPILE = THREE.Material.prototype.onBeforeCompile;
const CUSTOM_SHADER_KEYS = [
  'aerialPerspectiveInstalled',
  'valueCompositionInstalled',
  'asphaltShaderUniforms',
  'asphaltFastShaderUniforms',
  'kerbShaderUniforms',
  'terrainShaderMode',
] as const;

export function collectWebGPUCompatibilityReport(scene: THREE.Scene): WebGPUCompatibilityReport {
  const unsupported = new Map<string, WebGPUUnsupportedMaterial>();
  scene.traverse((object) => {
    const mesh = object as THREE.Object3D & { material?: THREE.Material | THREE.Material[] | null };
    const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    for (const material of materials) {
      const reasons = unsupportedReasons(material);
      if (reasons.length === 0) continue;
      const existing = unsupported.get(material.uuid);
      if (existing) {
        for (const reason of reasons) {
          if (!existing.reasons.includes(reason)) existing.reasons.push(reason);
        }
        continue;
      }
      unsupported.set(material.uuid, {
        uuid: material.uuid,
        name: material.name || '<unnamed>',
        type: material.type,
        objectPath: objectPath(object),
        reasons,
      });
    }
  });
  return { unsupportedMaterials: [...unsupported.values()] };
}

export function logWebGPUCompatibilityReport(report: WebGPUCompatibilityReport): void {
  if (report.unsupportedMaterials.length === 0) {
    console.info('[webgpu] no WebGL-only/custom shader materials detected');
    return;
  }
  const byReason = new Map<string, number>();
  for (const material of report.unsupportedMaterials) {
    for (const reason of material.reasons) {
      byReason.set(reason, (byReason.get(reason) ?? 0) + 1);
    }
  }
  const hasHardBlockers = report.unsupportedMaterials.some((material) => (
    material.reasons.includes('ShaderMaterial') || material.reasons.includes('RawShaderMaterial')
  ));
  const log = hasHardBlockers ? console.warn : console.info;
  log(
    hasHardBlockers
      ? `[webgpu] ${report.unsupportedMaterials.length} WebGL-only shader materials still need WebGPU material ports.`
      : `[webgpu] ${report.unsupportedMaterials.length} WebGL material shader enhancements still need TSL parity ports.`,
    {
      reasons: Object.fromEntries(byReason),
      samples: report.unsupportedMaterials.slice(0, 16),
    },
  );
}

export function mergeWebGPUCompatibilityReports(
  existing: WebGPUCompatibilityReport | null,
  next: WebGPUCompatibilityReport,
): WebGPUCompatibilityReport {
  if (!existing) return next;
  const merged = new Map<string, WebGPUUnsupportedMaterial>();
  for (const material of [...existing.unsupportedMaterials, ...next.unsupportedMaterials]) {
    const current = merged.get(material.uuid);
    if (!current) {
      merged.set(material.uuid, { ...material, reasons: [...material.reasons] });
      continue;
    }
    for (const reason of material.reasons) {
      if (!current.reasons.includes(reason)) current.reasons.push(reason);
    }
  }
  return { unsupportedMaterials: [...merged.values()] };
}

function unsupportedReasons(material: THREE.Material): string[] {
  const reasons: string[] = [];
  const flags = material as THREE.Material & {
    isShaderMaterial?: boolean;
    isRawShaderMaterial?: boolean;
    isNodeMaterial?: boolean;
    customProgramCacheKey?: () => string;
  };
  if (flags.isRawShaderMaterial) reasons.push('RawShaderMaterial');
  else if (flags.isShaderMaterial) reasons.push('ShaderMaterial');
  if (material.onBeforeCompile !== DEFAULT_ON_BEFORE_COMPILE) reasons.push('onBeforeCompile');
  if (
    !isWebGPUNodeMaterial(material)
    && typeof flags.customProgramCacheKey === 'function'
    && flags.customProgramCacheKey !== THREE.Material.prototype.customProgramCacheKey
  ) {
    reasons.push('customProgramCacheKey');
  }
  for (const key of CUSTOM_SHADER_KEYS) {
    if (isWebGPUSafeFallback(material, key)) continue;
    if (material.userData[key] !== undefined) reasons.push(`userData.${key}`);
  }
  return [...new Set(reasons)];
}

function isWebGPUNodeMaterial(material: THREE.Material): boolean {
  const candidate = material as THREE.Material & { isNodeMaterial?: boolean };
  return candidate.isNodeMaterial === true || material.type.endsWith('NodeMaterial');
}

function isWebGPUSafeFallback(material: THREE.Material, key: (typeof CUSTOM_SHADER_KEYS)[number]): boolean {
  if (key === 'aerialPerspectiveInstalled') return material.userData.aerialPerspectiveWebGPUFallback === true;
  if (key === 'valueCompositionInstalled') return material.userData.valueCompositionWebGPUFallback === true;
  if (key === 'terrainShaderMode') return material.userData.webgpuTerrainMaterial === true;
  if (key === 'asphaltShaderUniforms' || key === 'asphaltFastShaderUniforms') return material.userData.webgpuAsphaltMaterial === true;
  if (key === 'kerbShaderUniforms') return material.userData.webgpuKerbMaterial === true;
  return false;
}

function objectPath(object: THREE.Object3D): string {
  const parts: string[] = [];
  let current: THREE.Object3D | null = object;
  while (current) {
    parts.push(current.name || current.type);
    current = current.parent;
  }
  return parts.reverse().join('/');
}
